import { MessageSquare, ShoppingCart, Target, TrendingUp, Wallet } from 'lucide-react';
import type { KpiCardProps } from '@/components/ui/KpiCard';
import type { ToneName } from '@/theme';
import { countLabel, formatCount, formatNumber, formatPorcentaje } from './reports-format';
import type { ReporteResumen } from '@/types/reports';

/**
 * Piezas de interfaz que COMPARTEN el Panel (`/dashboard`) y Reportes (`/reports`).
 *
 * Existe por una razón concreta: las dos pantallas leen el mismo resumen del módulo 10 y enseñan los
 * mismos cinco indicadores. Si cada una tuviera su versión, el mismo «Conversaciones nuevas» podría
 * decir dos cosas distintas (un porcentaje aquí, un conteo allá) y nadie lo notaría hasta que un
 * cliente preguntara por qué los números no cuadran.
 *
 * Es un `.tsx` porque devuelve JSX (los iconos de cada tarjeta van dentro del objeto del KPI); todo lo
 * que es solo texto y números vive en `reports-format.ts`, que no depende de React y se puede probar
 * sin montar nada.
 */

/** Las cinco cifras del resumen, ya formateadas y con su explicación de una línea. */
export function kpisResumen(resumen: ReporteResumen): KpiCardProps[] {
  const { conversaciones, mensajes, ordenes, ventas, tasas } = resumen;

  return [
    {
      label: 'Conversaciones nuevas',
      value: formatCount(conversaciones.total),
      hint: `${formatCount(conversaciones.activas)} con actividad · ${countLabel(
        conversaciones.conOrden,
        'llegó a una orden',
        'llegaron a una orden',
      )}`,
      icon: <MessageSquare size={15} />,
      tone: 'brand' as ToneName,
    },
    {
      label: 'Mensajes',
      value: formatCount(mensajes.total),
      unit:
        mensajes.porConversacion === null
          ? undefined
          : `${formatNumber(mensajes.porConversacion)} por conversación`,
      hint: `${formatCount(mensajes.entrantes)} del cliente · ${formatCount(
        mensajes.salientes,
      )} enviados por el bot`,
      icon: <TrendingUp size={15} />,
      tone: 'info' as ToneName,
    },
    {
      label: 'Órdenes creadas',
      value: formatCount(ordenes.total),
      hint: `${formatCount(ordenes.pendientesConfirmacion)} esperando confirmación`,
      icon: <ShoppingCart size={15} />,
      tone: 'neutral' as ToneName,
      footnote:
        ordenes.canceladas > 0
          ? `${formatCount(ordenes.canceladas)} canceladas en el periodo`
          : undefined,
    },
    {
      label: 'Ventas confirmadas',
      value: formatCount(ordenes.ventasConfirmadas),
      hint: `${formatPorcentaje(tasas.conversionPorcentaje)} de las conversaciones del periodo`,
      icon: <Target size={15} />,
      tone: 'success' as ToneName,
      footnote:
        tasas.conversionPorcentaje === null
          ? 'Sin conversaciones en el periodo: la tasa no se puede calcular'
          : undefined,
    },
    {
      label: 'Ingresos',
      value: formatNumber(ventas.ingresos),
      unit:
        ventas.ticketPromedio === null
          ? undefined
          : `ticket ${formatNumber(ventas.ticketPromedio)}`,
      hint: 'Suma de las ventas confirmadas, sin símbolo de moneda',
      icon: <Wallet size={15} />,
      tone: 'brand' as ToneName,
      footnote:
        ventas.ticketPromedio === null && ordenes.ventasConfirmadas > 0
          ? 'Ticket promedio «—»: hay ventas sin total cargado'
          : undefined,
    },
  ];
}
